import React from 'react';
import {
  Map,
  useMap,
  MapControl,
  ControlPosition,
} from '@vis.gl/react-google-maps';
import MapLoadingSpinner from '../shared/MapLoadingSpinner';
import WeatherStationsMarkers from './WeatherStationsMarkers';
import {
  CContainer,
  CListGroup,
  CListGroupItem,
  CCard,
  CCardBody,
  CCardText,
  CFormSelect,
  CFormSwitch,
  CButton,
} from '@coreui/react-pro';
import WeatherStationModal from './WeatherStationModal';
import {
  initWeatherStations,
  toggleLocationBasedSorting,
  userSelectedWeatherStation,
  userSelectedWeatherStationDataPoint1,
  userSelectedWeatherStationDataPoint2,
  userSelectedWeatherStationDataPoint3,
} from './weatherStations-actions';
import moment from 'moment';
import {
  useDispatch,
  useSelector,
} from 'react-redux';

const WeatherStations = () => {

  const dispatch = useDispatch();

  const map = useMap();

  const {
    sidebarVisible,
    sidebarUnfoldable,
  } = useSelector(state => state.userSettings);

  const {
    dataPointDropDownListDataSource,
    selectedDataPoint1,
    selectedDataPoint2,
    selectedDataPoint3,
    weatherStations,
    locationEnabled,
    selectedWeatherStation,
    fetchingWeatherStations,
  } = useSelector(state => state.weatherStations);

  React.useEffect(() => {
    dispatch(initWeatherStations());
  }, [dispatch]); 

  const [windowWidth, setWindowWidth] = React.useState(window.innerWidth);
  const [windowHeight, setWindowHeight] = React.useState(window.innerHeight);

  React.useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      setWindowHeight(window.innerHeight);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize)
  }, []);

  const [isSidebarCollapsed, setIsSidebarCollapsed] = React.useState(false);

  React.useEffect(() => {
    if (selectedWeatherStation !== null && map !== null) {
      setTimeout(() => {
        map.panTo({
          lat: selectedWeatherStation.latitude,
          lng: selectedWeatherStation.longitude,
        });
        if (map.zoom >= 16) {
          return;
        }
        else {
          var zoomStep = map.zoom;
          const interval = setInterval(() => {
            if (zoomStep === 16) {
              clearInterval(interval);
            }
            map.setZoom(zoomStep++);
          }, 200);
        }
      }, 250);
    }
  }, [dispatch, selectedWeatherStation, map]);

  const height = windowHeight - 64;
  var width = windowWidth;

  if (sidebarUnfoldable === true && sidebarVisible === true) {
    width -= 64;
  }
  else if (sidebarUnfoldable === false && sidebarVisible === true) {
    width -= 256;
  }

  return (
    <div style={{flexDirection: 'row', display: 'flex',}}>
      <div style={{
        width: isSidebarCollapsed ? 0 : 300,
        height: height,
        transition: 'width 0.3s ease-in-out',
      }}>
        <div style={{
          width: 300,
          height: height,
          overflowY: 'auto',
        }}>
          <CContainer fluid>
            <CCard>
              <CCardBody>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'flex-start',
                  marginBottom: '10px'
                }}>
                </div>
                <div style={{display: isSidebarCollapsed ? 'none' : 'block'}}>
                  <CButton
                    className='mb-3'
                    color='primary'
                    variant='outline'
                    style={{
                      width: 140,
                      marginLeft: 40,
                    }}
                    onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}>
                    Expand Map
                  </CButton>
                  <CFormSelect
                    floatingLabel='Select Data Point'
                    className='mb-3'
                    style={{width: '100%', lineHeight: 4,}}
                    value={selectedDataPoint1}
                    options={dataPointDropDownListDataSource}
                    onChange={(e) => dispatch(userSelectedWeatherStationDataPoint1(e.target.value))} />
                  <CFormSelect
                    floatingLabel='Select Data Point'
                    className='mb-3'
                    style={{width: '100%', lineHeight: 4,}}
                    value={selectedDataPoint2}
                    options={dataPointDropDownListDataSource}
                    onChange={(e) => dispatch(userSelectedWeatherStationDataPoint2(e.target.value))} />
                  <CFormSelect
                    floatingLabel='Select Data Point'
                    className='mb-3'
                    style={{width: '100%', lineHeight: 4,}}
                    value={selectedDataPoint3}
                    options={dataPointDropDownListDataSource}
                    onChange={(e) => dispatch(userSelectedWeatherStationDataPoint3(e.target.value))} />
                  <CFormSwitch 
                    size='lg'
                    className='mb-3'
                    label='Sort By My Location' 
                    checked={locationEnabled}
                    onChange={() => dispatch(toggleLocationBasedSorting())} />
                  <CCardText>
                    Click on a Weather Station below to zoom to the station and see its data.
                  </CCardText>
                  <CListGroup>
                    {
                      weatherStations.map((station, index) => {
                        return (
                          <CListGroupItem 
                            key={station.stationId}
                            active={selectedWeatherStation?.stationId === station.stationId}
                            style={{cursor: 'pointer',}}
                            onClick={() => dispatch(userSelectedWeatherStation(station, false))}>
                            {station.stationName + (moment().diff(moment(station.observationDateTime), 'days') > 1 ? ' (Offline)' : '')}
                          </CListGroupItem>
                        );
                      })
                    }
                  </CListGroup>
                </div>
              </CCardBody>
            </CCard>
          </CContainer>
        </div>
      </div>
      <div style={{flexDirection: 'column', display: 'flex',}}>
        <div
          style={{
            width: width - (isSidebarCollapsed ? 0 : 300),
            height: height,
          }}>
          <Map
            mapId='WEATHER_STATIONS'
            defaultCenter={{
              lng: -88.257311,
              lat: 40.112519,
            }} 
            defaultZoom={parseInt(process.env.REACT_APP_DEFAULT_MAP_ZOOM)}
            fullscreenControl={false}
            mapTypeControl={true}
            streetViewControl={false}>
            {
              fetchingWeatherStations === true ? (
                <MapControl position={ControlPosition.CENTER}>
                  <MapLoadingSpinner loadingText='Fetching Weather Stations' />
                </MapControl>
              ) : null
            }
            {
              isSidebarCollapsed === true ? (
                <MapControl position={ControlPosition.TOP_LEFT}>
                  <CButton
                    color='primary'
                    variant='outline'
                    style={{
                      width: 140,
                      backgroundColor: 'white',
                      marginTop: 10,
                      marginLeft: 10,
                      color: 'black',
                    }}
                    onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}>
                    Show List
                  </CButton>
                </MapControl>
              ) : null
            }
            <WeatherStationsMarkers weatherStations={weatherStations} />
          </Map>
        </div>
        <WeatherStationModal />
      </div>
    </div>
  );
}

export default WeatherStations;
